/**
 * Service für Desktop-Icon-Positionen (Azure AD)
 * Speichert und lädt Icon-Positionen über das FastAPI Backend.
 */

import { logError, logInfo } from '@/lib/logger'
import { backendSettingsService } from './backend-settings-service'
import { authService } from './auth-service'
import type { IconPositions } from '@/types/settings'

export class IconPositionService {
  /**
   * Get current user from authService (Azure AD)
   */
  private static getCurrentUser() {
    return authService.getCachedUser()
  }

  /**
   * Lädt Icon-Positionen aus den Backend-Settings.
   * Wirft bei Backend-Fehlern (statt still ein leeres Objekt zu liefern),
   * damit Aufrufer nicht ein leeres Layout über das gespeicherte schreiben.
   */
  static async loadIconPositions(): Promise<IconPositions> {
    const user = this.getCurrentUser()
    if (!user) {
      logInfo('Kein Benutzer angemeldet, verwende leere Icon-Positionen')
      return {}
    }

    logInfo('Icon-Positionen werden aus Backend geladen')
    const settings = await backendSettingsService.loadSettings()
    return settings?.desktopIconPositions || {}
  }

  /**
   * Speichert Icon-Positionen über das Backend.
   * Nutzt PATCH, damit nur `desktopIconPositions` ersetzt wird — ein
   * Load-Modify-Write über alle Settings kann bei fehlgeschlagenem Load
   * andere Einstellungen (z. B. desktopInstalledApps) zerstören.
   */
  static async saveIconPositions(positions: IconPositions): Promise<boolean> {
    const user = this.getCurrentUser()
    if (!user) {
      logError(new Error('Kein Benutzer angemeldet'), { context: 'Icon-Positionen speichern' })
      return false
    }

    try {
      const success = await backendSettingsService.patchSettings({
        desktopIconPositions: positions,
      })

      if (success) {
        logInfo('Icon-Positionen erfolgreich im Backend gespeichert')
      } else {
        logError(new Error('Backend speichern fehlgeschlagen'), { context: 'Icon-Positionen speichern' })
      }

      return success
    } catch (error) {
      logError(error as Error, { context: 'Icon-Positionen speichern im Backend' })
      return false
    }
  }

  /**
   * Löscht alle Icon-Positionen
   */
  static async clearIconPositions(): Promise<boolean> {
    return await this.saveIconPositions({})
  }
}
